import { Button, Form, Modal, Select } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PhotoIcon from "../../../../assets/icons/photo.svg";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import useCarPricingMode from "../../hooks/useCarPricingMode";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";

const getCarImage = (car) => car?.images?.image1 || car?.images?.image2 || car?.images?.image3 || null;

const getCarLocation = (car) => {
    const location = car?.location;
    if (!location) return null;
    if (location.city && location.state) return `${location.city}, ${location.state}`;
    if (location.address) return location.address;
    return null;
};

const CarInfoField = ({ label, value, className = "" }) => (
    <div className={`border-b border-[#f0f0f0] px-4 py-3 last:border-b-0 ${className}`}>
        <p className="m-0 text-xs text-[#9b9ba5]">{label}</p>
        <p className="m-0 mt-1 break-words text-sm font-medium text-[#2F2F46]">{value || "N/A"}</p>
    </div>
);

CarInfoField.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
};

const CarInfoSummary = ({ car, t }) => {
    if (!car) return null;

    const carImage = getCarImage(car);
    const location = getCarLocation(car);
    const currentCategory = car.category?.categoryName || car.categoryName;

    return (
        <div className="mb-5 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white">
            <div className="flex justify-center border-b border-[#f0f0f0] bg-white px-3 py-4">
                <div className="flex h-[7.5rem] w-full max-w-[16rem] items-center justify-center overflow-hidden rounded-lg border border-[#ececf4] bg-[#f8f8fc] sm:h-[120px] sm:max-w-[10rem]">
                    {carImage ? (
                        <img
                            src={carImage}
                            alt={car.carName || t("carImage")}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-2 px-3 text-center">
                            <img src={PhotoIcon} alt="" className="h-8 w-8 opacity-40" />
                            <span className="text-[0.7rem] text-[#acacb9]">{t("carImage")}</span>
                        </div>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2">
                <CarInfoField label={t("carName")} value={car.carName} className="sm:col-span-2" />
                <CarInfoField label={t("vinNumberShort")} value={car.VIN} />
                <CarInfoField label={t("plateNumber")} value={car.carNumber} />
                <CarInfoField label={t("rates.currentCategory")} value={currentCategory} />
                <CarInfoField label={t("businessLocation")} value={location} />
            </div>
        </div>
    );
};

CarInfoSummary.propTypes = {
    car: PropTypes.object,
    t: PropTypes.func.isRequired,
};

const getCategoryDocumentId = (category) =>
    String(category._id ?? category.id ?? category.categoryId ?? "");

const buildCategoryUpdatePayload = (selectedCategory, isCarPricingMode) => {
    const categoryDocumentId = getCategoryDocumentId(selectedCategory);
    const payload = {
        category: categoryDocumentId,
        categoryId: selectedCategory.categoryId ?? categoryDocumentId,
        categoryName: selectedCategory.categoryName,
        bodyType: selectedCategory.categoryName,
    };

    if (!isCarPricingMode) {
        payload.config = {
            seats: selectedCategory.seats,
            bags: selectedCategory.bags,
        };
    }

    return payload;
};

const ExchangeCategoryModal = ({ open, onClose, car, currentCategoryId, onSuccess }) => {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const isCarPricingMode = useCarPricingMode();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        let cancelled = false;

        const fetchCategories = async () => {
            setCategoriesLoading(true);
            try {
                const response = await apiClient.get("/api/car-category-v2/list?limit=200");
                if (cancelled) return;

                if (response?.data?.success) {
                    setCategories(response.data.data || []);
                }
            } catch (_error) {
                if (!cancelled) {
                    toast.error({
                        message: t("rates.failedToFetchCategory"),
                        description: t("rates.unknownError"),
                    });
                }
            } finally {
                if (!cancelled) {
                    setCategoriesLoading(false);
                }
            }
        };

        fetchCategories();
        form.resetFields();

        return () => {
            cancelled = true;
        };
    }, [open, form, toast, t]);

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    const handleFinish = async (values) => {
        if (!car?.id) {
            return;
        }

        if (values.categoryId === currentCategoryId) {
            toast.warning({
                message: t("rates.exchangeCategorySameCategory"),
            });
            return;
        }

        const selectedCategory = categories.find((category) => category.categoryId === values.categoryId);
        if (!selectedCategory) {
            return;
        }

        setLoading(true);
        try {
            const payload = buildCategoryUpdatePayload(selectedCategory, isCarPricingMode);

            const response = await apiClient.patch(`/api/car/${car.id}`, payload);

            if (response?.data?.success) {
                toast.success({
                    message: t("success"),
                    description: t("rates.categoryExchangedSuccess"),
                });
                onSuccess?.();
                handleClose();
            } else {
                toast.error({
                    message: t("error"),
                    description: t("rates.failedToExchangeCategory"),
                });
            }
        } catch (error) {
            toast.error({
                message: t("error"),
                description: error.response?.data?.message || t("rates.failedToExchangeCategory"),
            });
        } finally {
            setLoading(false);
        }
    };

    const categoryOptions = categories
        .filter((category) => category.categoryId !== currentCategoryId)
        .map((category) => ({
            value: category.categoryId,
            label: category.categoryName,
        }));

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            maskClosable={false}
            centered
            title={t("rates.exchangeCategory")}
            width={{
                xs: "calc(100vw - 2rem)",
                sm: 560,
            }}
            footer={null}
            destroyOnHidden
            classNames={{ body: "min-w-0" }}
            styles={{
                body: { maxHeight: "min(80vh, 720px)", overflowY: "auto" },
            }}>
            <p className="mb-4 text-sm text-[#9b9ba5]">{t("rates.exchangeCategoryDescription")}</p>
            <CarInfoSummary car={car} t={t} />
            <Form
                form={form}
                layout="vertical"
                requiredMark={customizeRequiredMark}
                onFinish={handleFinish}>
                <Form.Item
                    name="categoryId"
                    label={t("selectCategory")}
                    rules={[{ required: true, message: t("rates.categoryRequired") }]}>
                    <Select
                        showSearch
                        placeholder={t("selectCategory")}
                        optionFilterProp="label"
                        loading={categoriesLoading}
                        options={categoryOptions}
                        className="w-full"
                    />
                </Form.Item>
                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button onClick={handleClose} className="!h-11 w-full !rounded-xl sm:!h-8 sm:w-auto">
                        {t("cancel")}
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        className="!h-11 w-full !rounded-xl sm:!h-8 sm:w-auto">
                        {t("rates.save")}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};

ExchangeCategoryModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    car: PropTypes.object,
    currentCategoryId: PropTypes.string,
    onSuccess: PropTypes.func,
};

export default ExchangeCategoryModal;
